import type { ReactNode } from 'react';
import { Button, IconButton } from '@viquard/doc-ui/components';
import { IcnArrowLeft, IcnArrowRight } from '@viquard/doc-ui/icons';
import { useCalendarMessages } from '../i18n.js';

export interface CalendarHeaderProps {
  readonly label: string;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly onToday?: (() => void) | undefined;
  readonly prevLabel?: string | undefined;
  readonly nextLabel?: string | undefined;
  readonly todayLabel?: string | undefined;
}

/** Header row used by Month/Week/Day views: prev / month-label / next / today.
 *  String defaults come from the CalendarI18nContext (English when no
 *  provider is mounted) so consumers that pass `messages` to CalendarView
 *  get localised arrows + Today affordance for free. Explicit prop
 *  overrides still win. */
export function CalendarHeader(props: CalendarHeaderProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    label,
    onPrev,
    onNext,
    onToday,
    prevLabel = messages.prev,
    nextLabel = messages.next,
    todayLabel = messages.today,
  } = props;
  return (
    <div className="vq-cal__header">
      <IconButton label={prevLabel} size="sm" variant="ghost" onClick={onPrev}>
        <IcnArrowLeft size={16} />
      </IconButton>
      <h2 className="vq-cal__header-label">{label}</h2>
      <IconButton label={nextLabel} size="sm" variant="ghost" onClick={onNext}>
        <IcnArrowRight size={16} />
      </IconButton>
      {onToday !== undefined && (
        <Button
          className="vq-cal__header-today"
          variant="ghost"
          size="sm"
          shape="pill"
          onClick={onToday}
        >
          {todayLabel}
        </Button>
      )}
    </div>
  );
}
